import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LayoutDashboard, LogOut, Wallet } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useOwnerAuth } from "@/components/site/useOwnerAuth";
import { OwnerLoginScreen } from "@/components/site/OwnerLoginScreen";
import { LangToggle } from "@/components/site/LangToggle";
import { ExpenseForm } from "@/components/expenses/ExpenseForm";
import { ExpenseList } from "@/components/expenses/ExpenseList";
import { MonthStats } from "@/components/expenses/MonthStats";
import { CATEGORIES, CATEGORY_COLORS, currentMonth, money } from "@/components/expenses/expenseUtils";

export default function Expenses() {
  const navigate = useNavigate();
  const { t } = useLang();
  const { user, auth, forbidden, setForbidden } = useOwnerAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (auth !== true) return;
    const load = async () => {
      const res = await fetch(`${API}/owner/expenses`, { credentials: "include" });
      if (res.status === 403) {
        setForbidden(true);
        return;
      }
      if (res.ok) setItems(await res.json());
    };
    load();
  }, [auth, setForbidden]);

  const addExpense = async (payload) => {
    try {
      const res = await fetch(`${API}/owner/expenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setItems((list) =>
        [saved, ...list].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
      );
      toast.success(`${t("exp_added")} · ${money(saved.amount)}`);
      return true;
    } catch {
      toast.error(t("exp_fail"));
      return false;
    }
  };

  const deleteExpense = async (e) => {
    const prev = items;
    setItems((list) => list.filter((x) => x.id !== e.id));
    try {
      const res = await fetch(`${API}/owner/expenses/${e.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      toast.success(t("exp_removed"));
    } catch {
      setItems(prev);
      toast.error(t("exp_fail"));
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    toast.success(t("dash_signed_out"));
    navigate("/");
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-[#0E1A2B] flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400 animate-pulse">
          {t("dash_checking")}
        </p>
      </div>
    );
  }

  if (auth === false || forbidden) {
    return <OwnerLoginScreen forbidden={forbidden} path="/gastos" testId="expenses-login-screen" />;
  }

  const month = currentMonth();
  const monthItems = items.filter((e) => e.date.startsWith(month));
  const shown = filter === "all" ? items : items.filter((e) => e.category === filter);
  const shownTotal = shown.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="min-h-screen bg-[#0E1A2B] grain relative" data-testid="expenses-page">
      <header className="border-b border-white/10 bg-[#0E1A2B]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="px-4 sm:px-8 lg:px-12 max-w-5xl mx-auto flex items-center justify-between gap-3 h-20">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/logo.png"
              alt="PERETE logo"
              className="h-11 w-11 shrink-0 rounded-full ring-2 ring-[#F26522]/70 object-cover"
            />
            <div className="leading-tight min-w-0">
              <span className="block font-display font-extrabold text-white tracking-tight truncate">
                {t("exp_title")}
              </span>
              <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] text-[#F26522] truncate">
                {user?.email}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <LangToggle />
            <button
              onClick={() => navigate("/dashboard")}
              aria-label={t("exp_dashboard")}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 text-slate-300 hover:text-white hover:border-[#F26522] px-3 sm:px-4 py-2 min-h-[40px] text-sm font-semibold transition-colors duration-200"
              data-testid="expenses-dashboard-btn"
            >
              <LayoutDashboard className="h-4 w-4" /> <span className="hidden sm:inline">{t("exp_dashboard")}</span>
            </button>
            <button
              onClick={logout}
              aria-label={t("dash_signout")}
              className="inline-flex items-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white px-3 sm:px-4 py-2 min-h-[40px] text-sm font-bold transition-[background-color,transform] duration-200 hover:scale-[1.03] active:scale-95"
              data-testid="expenses-logout-btn"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash_signout")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-8 lg:px-12 max-w-5xl mx-auto py-10 sm:py-14">
        <div className="mb-8 flex items-start gap-3">
          <Wallet className="h-7 w-7 text-[#F26522] mt-1" />
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              {t("exp_title")}
            </h1>
            <p className="mt-1 text-sm text-slate-400">{t("exp_sub")}</p>
          </div>
        </div>

        <MonthStats monthItems={monthItems} />

        <section className="rounded-3xl bg-[#0B1524] border border-[#203859] overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-[#203859]">
            <ExpenseForm onAdd={addExpense} />
          </div>

          <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-4 border-b border-[#203859]" data-testid="expense-filters">
            {["all", ...CATEGORIES].map((c) => {
              const active = filter === c;
              return (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 min-h-[36px] font-mono text-[11px] font-bold uppercase tracking-[0.12em] border transition-[background-color,color,border-color] duration-200 ${
                    active
                      ? "bg-white text-[#0E1A2B] border-white"
                      : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
                  }`}
                  data-testid={`expense-filter-${c}`}
                >
                  {c !== "all" && (
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[c] }} />
                  )}
                  {c === "all" ? t("exp_all") : t(`cat_${c}`)}
                </button>
              );
            })}
            <span className="w-full sm:w-auto sm:ml-auto pt-2 sm:pt-0 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500" data-testid="expense-filtered-total">
              {t("exp_filtered_total")}: <span className="text-white font-bold">{money(shownTotal)}</span>
              <span className="text-slate-600"> · {shown.length} {t("exp_entries")}</span>
            </span>
          </div>

          <ExpenseList items={shown} hasAny={items.length > 0} onDelete={deleteExpense} />
        </section>

        <button
          onClick={() => navigate("/")}
          className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#F26522] transition-colors duration-200"
          data-testid="expenses-back-home-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("dash_back")}
        </button>
      </main>
    </div>
  );
}
